import { useShallow } from 'zustand/shallow';
import { useGcsStore } from '../../../store/useGcsStore';
import { useFleetVisibilityStore } from '../../../store/useFleetVisibilityStore';
import { useSelectedVehicleStore } from '../../../store/useSelectedVehicleStore';
import { selectIsVehicleUnderMyControl } from '../../../store/selectors/selectIsVehicleUnderMyControl';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
import { VehicleStatusCategory } from '../../../types/ui/vehicleStatusCategory';
import { SidePanelVehicleTile } from './SidePanelVehicleTile';
import SidePanelHeaderTile from './SidePanelHeaderTile';
import FloatingCard from '../../../components/general/FloatingCard';
import FleetVisibilitySegmentedControl from './FleetVisibilitySegmentedControl';
import { getShortVehicleId } from '../../../utils/formatters';
import { deserializeGcsVehicleId, serializeGcsVehicleId } from '../../../utils/gcsVehicleId';
import { en } from '../../../locales/en';
import { Text } from '../../../components/general/Text';

function SidePanel() {
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);
  const toggleVehicle = useSelectedVehicleStore((state) => state.toggleVehicle);
  const visibility = useFleetVisibilityStore((state) => state.visibility);

  // Build vehicle list in natural order, sorted by category and relevant timestamp,
  // and apply visibility filtering reactively.
  const gcsVehicleIds = useGcsStore(
    useShallow((state) => {
      const vehicles: Array<{
        gcsVehicleId: string;
        category: VehicleStatusCategory;
        sortTimestamp: string | null;
        vehicleLabel: string;
      }> = [];

      state.data.forEach((gcsData, gcsId) => {
        gcsData.vehicles.data?.forEach((vehicle) => {
          const vehicleState = gcsData.vehicleStateMap.get(vehicle.id);
          if (vehicleState && !vehicleState.isInService) return;

          const category = selectVehicleStatusCategory(state.data, gcsId, vehicle.id);
          if (!category) return;
          const liveMission = gcsData.liveMissionsMap.get(vehicle.id);
          const missionTimestamps = liveMission ? gcsData.missionTimestampsMap.get(liveMission.missionId) : undefined;

          // Pick the relevant timestamp based on category
          let sortTimestamp: string | null = null;
          if (missionTimestamps) {
            switch (category) {
              case VehicleStatusCategory.INFLIGHT_FAILURE_MODE:
                sortTimestamp = missionTimestamps.initialFailureModeTimestamp ?? null;
                break;
              case VehicleStatusCategory.INFLIGHT_NOMINAL:
                sortTimestamp = missionTimestamps.takeoffTimestamp ?? null;
                break;
              case VehicleStatusCategory.READY_FOR_TAKEOFF:
                sortTimestamp = missionTimestamps.mcPreflightTimestamp ?? null;
                break;
              case VehicleStatusCategory.WAITING_ON_DOCK:
                sortTimestamp = missionTimestamps.dockLandingTimestamp ?? null;
                break;
            }
          }

          vehicles.push({
            gcsVehicleId: serializeGcsVehicleId(gcsId, vehicle.id),
            category,
            sortTimestamp,
            vehicleLabel: getShortVehicleId(vehicle.id)
          });
        });
      });

      // Define category priority (lower number = higher priority)
      const getCategoryPriority = (category: VehicleStatusCategory): number => {
        switch (category) {
          case VehicleStatusCategory.INFLIGHT_FAILURE_MODE:
            return 0;
          case VehicleStatusCategory.INFLIGHT_NOMINAL:
            return 2;
          case VehicleStatusCategory.READY_FOR_TAKEOFF:
            return 1;
          case VehicleStatusCategory.WAITING_ON_DOCK:
            return 3;
          case VehicleStatusCategory.POSTFLIGHT:
            return 4;
          case VehicleStatusCategory.ON_GROUND:
            return 5;
        }
      };

      // Sort vehicles
      vehicles.sort((a, b) => {
        // First, sort by category priority
        const categoryDiff = getCategoryPriority(a.category) - getCategoryPriority(b.category);
        if (categoryDiff !== 0) return categoryDiff;

        // Within same category, sort by relevant timestamp (earlier first, ISO string comparison)
        if (a.sortTimestamp && b.sortTimestamp) {
          if (a.sortTimestamp < b.sortTimestamp) return -1;
          if (a.sortTimestamp > b.sortTimestamp) return 1;
          return 0;
        }

        // Fallback: sort by vehicle label
        return a.vehicleLabel.localeCompare(b.vehicleLabel);
      });

      const allIds = vehicles.map((v) => v.gcsVehicleId);

      if (visibility === 'all') {
        return allIds;
      }

      return allIds.filter((id) => {
        const { gcsId, vehicleId } = deserializeGcsVehicleId(id);
        return selectIsVehicleUnderMyControl(state.data, gcsId, vehicleId);
      });
    })
  );

  return (
    <FloatingCard>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: '100%',
          overflow: 'visible'
        }}
      >
        <SidePanelHeaderTile />

        <FleetVisibilitySegmentedControl />

        <div
          style={{
            overflow: 'auto',
            flex: 1,
            border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
            borderRadius: 'var(--card-border-radius)',
            maxHeight: 'calc(100vh - 152px)' // Constrain height to enable scrolling
          }}
        >
          {gcsVehicleIds.length > 0 ? (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}
            >
              {gcsVehicleIds.map((id) => {
                const { gcsId, vehicleId } = deserializeGcsVehicleId(id);
                const isSelected = selectedVehicle?.vehicleId === vehicleId && selectedVehicle?.gcsId === gcsId;
                return (
                  <SidePanelVehicleTile
                    key={id}
                    gcsId={gcsId}
                    vehicleId={vehicleId}
                    isSelected={isSelected}
                    onPress={() => toggleVehicle(vehicleId, gcsId)}
                  />
                );
              })}
            </ul>
          ) : (
            <div
              style={{
                padding: '32px 16px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              <Text variant='body2' style={{ color: 'var(--text-secondary)' }}>
                {en.sidePanel.emptyState}
              </Text>
            </div>
          )}
        </div>
      </div>
    </FloatingCard>
  );
}

export default SidePanel;
